'use client';
import { useState, useEffect } from 'react';

interface TechCompliance {
  techSlug: string;
  techName: string;
  market: string;
  services: number;
  texted: number;
  missed: number;
  needsPhone?: number;
  excused?: number;
  /** null when nothing on this day was the tech's to text (all excused / no phone). */
  rate: number | null;
  countable?: number;
  avgPhotos: number;
  avgResponseTime: string;
}

interface ServiceDetail {
  id: string;
  customerName: string;
  address: string;
  timeCompleted: string;
  textSent: boolean;
  excused?: boolean;
  excusedReason?: string | null;
  missReason?: string | null;
  textPreview: string;
  photos: string[];
  responseTime: string;
}

// Today in Central Time as YYYY-MM-DD. en-CA is evaluated directly in the requested
// zone; round-tripping through toISOString() would use the browser's own timezone and
// roll the date forward a day from 7pm CT onward.
function todayCentral(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

interface ComplianceTableProps {
  techs: TechCompliance[];
  loading: boolean;
  date: string;
  branch: string;
  leader: string;
  onPhotoClick: (photos: string[], index: number) => void;
}

type SortKey = 'techName' | 'market' | 'services' | 'texted' | 'missed' | 'excused' | 'rate' | 'avgPhotos';
type SortDir = 'asc' | 'desc';

export default function ComplianceTable({ techs, loading, date, branch, leader, onPhotoClick }: ComplianceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('rate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedTech, setExpandedTech] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceDetail[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'rate' ? 'asc' : 'desc'); }
  };

  // Techs with no rate (nothing they could have texted) always sort to the
  // bottom, in both directions. Treating null as 0 put them at the top of the
  // default worst-first sort, which is how Jordan led the board on a day he
  // had a single excused stop.
  const sorted = [...techs].sort((a, b) => {
    if (sortKey === 'rate') {
      if (a.rate == null && b.rate == null) return b.services - a.services;
      if (a.rate == null) return 1;
      if (b.rate == null) return -1;
    }
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    const cmp = typeof av === 'string' ? (av as string).localeCompare(bv as string) : (av as number) - (bv as number);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  useEffect(() => {
    if (!expandedTech) return;
    setServicesLoading(true);
    const params = new URLSearchParams({ date, techSlug: expandedTech, limit: '100', textSent: 'all' });
    if (branch !== 'All') params.set('branch', branch);
    if (leader !== 'All') params.set('leader', leader);
    fetch(`/api/dashboard/services?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setServices(d.services || []))
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, [expandedTech, date, branch, leader]);

  const rateColor = (r: number | null) => r == null ? 'text-gray-400' : r >= 90 ? 'text-green-600' : r >= 70 ? 'text-orange-500' : 'text-red-600';
  const barColor = (r: number | null) => r == null ? 'bg-gray-200' : r >= 90 ? 'bg-green-500' : r >= 70 ? 'bg-orange-400' : 'bg-red-500';
  const noRateTitle = 'No stops to text — every stop was excused or had no phone number on file. Not scored.';
  const SortIcon = ({ k }: { k: SortKey }) => sortKey === k ? <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="h-5 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-gray-100 rounded mb-2 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Tech Compliance</h2>
      </div>
      {techs.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          {date === todayCentral() ? (
            <>
              <div className="text-gray-500">No stops closed out yet today.</div>
              <div className="text-xs mt-1">
                The board fills in as techs complete their first stops — usually from about 7:45am CT.
                Use the date picker above to review an earlier day.
              </div>
            </>
          ) : (
            <>
              <div className="text-gray-500">No technician data for {date}.</div>
              <div className="text-xs mt-1">Nothing was completed on this date, or it falls outside your markets.</div>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                {([['techName', 'Tech'], ['market', 'Market'], ['services', 'Services'], ['texted', 'Texted'], ['missed', 'Missed'], ['excused', 'Excused'], ['rate', 'Rate'], ['avgPhotos', 'Avg Photos']] as [SortKey, string][]).map(([k, label]) => (
                  <th key={k} className="px-4 py-3 cursor-pointer hover:text-gray-700 whitespace-nowrap" onClick={() => handleSort(k)}>
                    {label}<SortIcon k={k} />
                  </th>
                ))}
                <th className="px-4 py-3 hidden lg:table-cell">Compliance</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(tech => (
                <>
                  <tr
                    key={tech.techSlug}
                    className={`border-t border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${tech.missed > 0 ? 'border-l-4 border-l-red-400' : ''}`}
                    onClick={() => setExpandedTech(expandedTech === tech.techSlug ? null : tech.techSlug)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {tech.techName}
                      {(tech.needsPhone ?? 0) > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-[10px] font-medium px-1.5 py-0.5 align-middle" title="Stops with no phone number on file — not counted against this tech">
                          📵 {tech.needsPhone} need #
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{tech.market}</td>
                    <td className="px-4 py-3">{tech.services}</td>
                    <td className="px-4 py-3 text-green-600">{tech.texted}</td>
                    <td className="px-4 py-3 text-red-500">{tech.missed}</td>
                    <td className="px-4 py-3 text-amber-500">{tech.excused || 0}</td>
                    <td className={`px-4 py-3 font-semibold ${rateColor(tech.rate)}`} title={tech.rate == null ? noRateTitle : undefined}>
                      {tech.rate == null ? '—' : `${Math.round(tech.rate)}%`}
                    </td>
                    <td className="px-4 py-3">{tech.avgPhotos?.toFixed(1) ?? '—'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell w-32">
                      {tech.rate == null ? (
                        <span className="text-xs text-gray-400" title={noRateTitle}>nothing to text</span>
                      ) : (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${barColor(tech.rate)}`} style={{ width: `${Math.min(tech.rate, 100)}%` }} />
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedTech === tech.techSlug && (
                    <tr key={`${tech.techSlug}-detail`}>
                      <td colSpan={9} className="bg-gray-50 px-4 py-3">
                        {servicesLoading ? (
                          <div className="text-center py-4 text-gray-400">Loading services...</div>
                        ) : services.length === 0 ? (
                          <div className="text-center py-4 text-gray-400">No services found</div>
                        ) : (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {services.map((svc, i) => {
                              const noPhone = !svc.textSent && !svc.excused && (svc.missReason || '').toLowerCase().includes('no phone');
                              return (
                              <div key={svc.id || i} className={`flex flex-wrap items-start gap-3 p-3 rounded-lg ${svc.excused && !svc.textSent ? 'bg-amber-50 border border-amber-200' : noPhone ? 'bg-blue-50 border border-blue-200' : !svc.textSent ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-100'}`}>
                                <div className="text-lg">{svc.textSent ? '✅' : svc.excused ? '⚠️' : noPhone ? '📵' : '❌'}</div>
                                <div className="flex-1 min-w-[200px]">
                                  <div className="font-medium text-gray-800">{svc.customerName}</div>
                                  {svc.excused && !svc.textSent && (
                                    <div className="text-xs font-medium text-amber-600">Couldn&apos;t service — {svc.excusedReason || 'no reason given'}</div>
                                  )}
                                  {!svc.textSent && !svc.excused && svc.missReason && (
                                    <div className={`text-xs font-medium ${noPhone ? 'text-blue-600' : 'text-red-600'}`}>{svc.missReason}</div>
                                  )}
                                  <div className="text-xs text-gray-500">{svc.address}</div>
                                  <div className="text-xs text-gray-400 mt-0.5">Completed: {svc.timeCompleted}{svc.responseTime ? ` · Response: ${svc.responseTime}` : ''}</div>
                                  {svc.textPreview && <div className="text-xs text-gray-600 mt-1 italic">&ldquo;{svc.textPreview}&rdquo;</div>}
                                </div>
                                {svc.photos?.length > 0 && (
                                  <div className="flex gap-1">
                                    {svc.photos.map((p, pi) => (
                                      <img
                                        key={pi}
                                        src={p}
                                        alt=""
                                        className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200"
                                        onClick={e => { e.stopPropagation(); onPhotoClick(svc.photos, pi); }}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export type { TechCompliance };
